
/**
 * TypeScript definitions for timezone-aware scheduling
 */

export interface TimezoneInfo {
  name: string;
  abbreviation: string;
  offset: string;
  offsetMinutes: number;
  isDST: boolean;
  currentTime: string;
}

export interface TimezoneAwareDateTime {
  utc: Date;
  local: Date;
  timezone: string;
  formatted: string;
  isValid: boolean;
}

export interface AvailabilityWindow {
  dayOfWeek: number; // 0-6 (Sunday-Saturday)
  startTime: string; // "09:00"
  endTime: string;   // "17:00"
  timezone: string;
}

export interface AppointmentData {
  id: string;
  startTimeUtc: string; // Always UTC ISO string
  duration: number; // minutes
  organizerTimezone: string;
  attendeeTimezone?: string;
  createdTimezone: string;
}

export interface TimezoneDisplayOption {
  timezone: string;
  time: string;
  info: TimezoneInfo;
}

export interface SchedulingRequest {
  dateTime: string;
  timezone: string;
  duration?: number;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

// Comprehensive timezone list organized by region - using only valid IANA timezone identifiers
export const COMPREHENSIVE_TIMEZONES = [
  // UTC
  'UTC',
  
  // North America
  'America/New_York',
  'America/Chicago', 
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'America/Adak',
  'America/Toronto',
  'America/Vancouver',
  'America/Montreal',
  'America/Halifax',
  'America/Mexico_City',
  'America/Cancun',
  'America/Tijuana',
  
  // South America
  'America/Sao_Paulo',
  'America/Buenos_Aires',
  'America/Lima',
  'America/Bogota',
  'America/Santiago',
  'America/Caracas',
  'America/La_Paz',
  'America/Montevideo',
  
  // Europe
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Vienna',
  'Europe/Zurich',
  'Europe/Stockholm',
  'Europe/Copenhagen',
  'Europe/Helsinki',
  'Europe/Oslo',
  'Europe/Warsaw',
  'Europe/Prague',
  'Europe/Budapest',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Europe/Kiev',
  'Europe/Bucharest',
  'Europe/Sofia',
  'Europe/Dublin',
  'Europe/Lisbon',
  
  // Asia
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Seoul',
  'Asia/Singapore',
  'Asia/Bangkok',
  'Asia/Jakarta',
  'Asia/Manila',
  'Asia/Kuala_Lumpur',
  'Asia/Kolkata',          // Valid India timezone
  'Asia/Dhaka',
  'Asia/Karachi',
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Tehran',
  'Asia/Jerusalem',
  'Asia/Beirut',
  'Asia/Baghdad',
  'Asia/Kuwait',
  'Asia/Qatar',
  'Asia/Muscat',
  'Asia/Baku',
  'Asia/Yerevan',
  'Asia/Tbilisi',
  'Asia/Almaty',
  'Asia/Tashkent',
  'Asia/Bishkek',
  'Asia/Dushanbe',
  'Asia/Ashgabat',
  'Asia/Kabul',
  'Asia/Colombo',
  'Asia/Kathmandu',
  'Asia/Thimphu',
  'Asia/Yangon',
  'Asia/Phnom_Penh',
  'Asia/Vientiane',
  'Asia/Ho_Chi_Minh',
  'Asia/Taipei',
  'Asia/Macau',
  
  // Africa
  'Africa/Cairo',
  'Africa/Lagos',
  'Africa/Johannesburg',
  'Africa/Nairobi',
  'Africa/Casablanca',
  'Africa/Algiers',
  'Africa/Tunis',
  'Africa/Tripoli',
  'Africa/Khartoum',
  'Africa/Addis_Ababa',
  'Africa/Dar_es_Salaam',
  'Africa/Kampala',
  'Africa/Kigali',
  'Africa/Lusaka',
  'Africa/Harare',
  'Africa/Maputo',
  'Africa/Windhoek',
  'Africa/Gaborone',
  'Africa/Maseru',
  'Africa/Mbabane',
  'Africa/Accra',
  'Africa/Abidjan',
  'Africa/Dakar',
  'Africa/Bamako',
  'Africa/Conakry',
  'Africa/Freetown',
  'Africa/Monrovia',
  
  // Oceania
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Darwin',
  'Australia/Hobart',
  'Pacific/Auckland',       // Valid New Zealand timezone
  'Pacific/Fiji',
  'Pacific/Tahiti',
  'Pacific/Honolulu',
  'Pacific/Guam',
  'Pacific/Port_Moresby',
  'Pacific/Noumea',
  'Pacific/Apia',           // Valid Samoa timezone
  'Pacific/Tongatapu',      // Valid Tonga timezone
  'Pacific/Kiritimati'
] as const;

export type ComprehensiveTimezone = typeof COMPREHENSIVE_TIMEZONES[number];

// Enhanced timezone groups with valid timezone identifiers only
export const ENHANCED_TIMEZONE_GROUPS = {
  'UTC': ['UTC'],
  'North America': [
    'America/New_York',      // Eastern Time
    'America/Chicago',       // Central Time  
    'America/Denver',        // Mountain Time
    'America/Los_Angeles',   // Pacific Time
    'America/Phoenix',       // Arizona (no DST)
    'America/Anchorage',     // Alaska Time
    'America/Adak',          // Hawaii-Aleutian Time
    'America/Toronto',       // Eastern Canada
    'America/Vancouver',     // Pacific Canada
    'America/Montreal',      // Eastern Canada
    'America/Halifax',       // Atlantic Canada
    'America/Mexico_City',   // Central Mexico
    'America/Cancun',        // Eastern Mexico
    'America/Tijuana'        // Pacific Mexico
  ],
  'South America': [
    'America/Sao_Paulo',     // Brazil (UTC-3)
    'America/Buenos_Aires',  // Argentina
    'America/Lima',          // Peru
    'America/Bogota',        // Colombia
    'America/Santiago',      // Chile
    'America/Caracas',       // Venezuela
    'America/La_Paz',        // Bolivia
    'America/Montevideo'     // Uruguay
  ],
  'Europe': [
    'Europe/London',         // UK
    'Europe/Paris',          // France
    'Europe/Berlin',         // Germany
    'Europe/Rome',           // Italy
    'Europe/Madrid',         // Spain
    'Europe/Amsterdam',      // Netherlands
    'Europe/Brussels',       // Belgium
    'Europe/Vienna',         // Austria
    'Europe/Zurich',         // Switzerland
    'Europe/Stockholm',      // Sweden
    'Europe/Copenhagen',     // Denmark
    'Europe/Helsinki',       // Finland
    'Europe/Oslo',           // Norway
    'Europe/Warsaw',         // Poland
    'Europe/Prague',         // Czech Republic
    'Europe/Budapest',       // Hungary
    'Europe/Athens',         // Greece
    'Europe/Istanbul',       // Turkey
    'Europe/Moscow',         // Russia
    'Europe/Kiev',           // Ukraine
    'Europe/Bucharest',      // Romania
    'Europe/Sofia',          // Bulgaria
    'Europe/Dublin',         // Ireland
    'Europe/Lisbon'          // Portugal
  ],
  'Asia': [
    'Asia/Tokyo',            // Japan
    'Asia/Shanghai',         // China
    'Asia/Hong_Kong',        // Hong Kong
    'Asia/Seoul',            // South Korea
    'Asia/Singapore',        // Singapore
    'Asia/Bangkok',          // Thailand
    'Asia/Jakarta',          // Indonesia
    'Asia/Manila',           // Philippines
    'Asia/Kuala_Lumpur',     // Malaysia
    'Asia/Kolkata',          // India (primary - valid timezone)
    'Asia/Dhaka',            // Bangladesh
    'Asia/Karachi',          // Pakistan
    'Asia/Dubai',            // UAE
    'Asia/Riyadh',           // Saudi Arabia
    'Asia/Tehran',           // Iran
    'Asia/Jerusalem',        // Israel
    'Asia/Beirut',           // Lebanon
    'Asia/Baghdad',          // Iraq
    'Asia/Kuwait',           // Kuwait
    'Asia/Qatar',            // Qatar
    'Asia/Muscat',           // Oman
    'Asia/Baku',             // Azerbaijan
    'Asia/Yerevan',          // Armenia
    'Asia/Tbilisi',          // Georgia
    'Asia/Almaty',           // Kazakhstan
    'Asia/Tashkent',         // Uzbekistan
    'Asia/Bishkek',          // Kyrgyzstan
    'Asia/Dushanbe',         // Tajikistan
    'Asia/Ashgabat',         // Turkmenistan
    'Asia/Kabul',            // Afghanistan
    'Asia/Colombo',          // Sri Lanka
    'Asia/Kathmandu',        // Nepal
    'Asia/Thimphu',          // Bhutan
    'Asia/Yangon',           // Myanmar
    'Asia/Phnom_Penh',       // Cambodia
    'Asia/Vientiane',        // Laos
    'Asia/Ho_Chi_Minh',      // Vietnam
    'Asia/Taipei',           // Taiwan
    'Asia/Macau'             // Macau
  ],
  'Africa': [
    'Africa/Cairo',          // Egypt
    'Africa/Lagos',          // Nigeria
    'Africa/Johannesburg',   // South Africa
    'Africa/Nairobi',        // Kenya
    'Africa/Casablanca',     // Morocco
    'Africa/Algiers',        // Algeria
    'Africa/Tunis',          // Tunisia
    'Africa/Tripoli',        // Libya
    'Africa/Khartoum',       // Sudan
    'Africa/Addis_Ababa',    // Ethiopia
    'Africa/Dar_es_Salaam',  // Tanzania
    'Africa/Kampala',        // Uganda
    'Africa/Kigali',         // Rwanda
    'Africa/Lusaka',         // Zambia
    'Africa/Harare',         // Zimbabwe
    'Africa/Maputo',         // Mozambique
    'Africa/Windhoek',       // Namibia
    'Africa/Gaborone',       // Botswana
    'Africa/Maseru',         // Lesotho
    'Africa/Mbabane',        // Eswatini
    'Africa/Accra',          // Ghana
    'Africa/Abidjan',        // Ivory Coast
    'Africa/Dakar',          // Senegal
    'Africa/Bamako',         // Mali
    'Africa/Conakry',        // Guinea
    'Africa/Freetown',       // Sierra Leone
    'Africa/Monrovia'        // Liberia
  ],
  'Oceania': [
    'Australia/Sydney',      // Eastern Australia
    'Australia/Melbourne',   // Eastern Australia
    'Australia/Brisbane',    // Eastern Australia (no DST)
    'Australia/Perth',       // Western Australia
    'Australia/Adelaide',    // Central Australia
    'Australia/Darwin',      // Central Australia (no DST)
    'Australia/Hobart',      // Tasmania
    'Pacific/Auckland',      // New Zealand (valid timezone)
    'Pacific/Fiji',          // Fiji
    'Pacific/Tahiti',        // French Polynesia
    'Pacific/Honolulu',      // Hawaii
    'Pacific/Guam',          // Guam
    'Pacific/Port_Moresby',  // Papua New Guinea
    'Pacific/Noumea',        // New Caledonia
    'Pacific/Apia',          // Samoa (valid timezone)
    'Pacific/Tongatapu',     // Tonga (valid timezone)
    'Pacific/Kiritimati'     // Line Islands
  ]
} as const;

// City name mappings for search functionality - updated with valid timezones
export const TIMEZONE_CITY_MAPPINGS: Record<string, string[]> = {
  'UTC': ['UTC', 'Universal', 'Greenwich'],
  'America/New_York': ['New York', 'NYC', 'Eastern', 'EST', 'EDT'],
  'America/Chicago': ['Chicago', 'Central', 'CST', 'CDT'],
  'America/Denver': ['Denver', 'Mountain', 'MST', 'MDT'],
  'America/Los_Angeles': ['Los Angeles', 'LA', 'Pacific', 'PST', 'PDT'],
  'America/Phoenix': ['Phoenix', 'Arizona', 'MST'],
  'America/Anchorage': ['Anchorage', 'Alaska', 'AKST', 'AKDT'],
  'America/Toronto': ['Toronto', 'Canada'],
  'America/Vancouver': ['Vancouver', 'Canada'],
  'America/Mexico_City': ['Mexico City', 'Mexico'],
  'Europe/London': ['London', 'UK', 'Britain', 'GMT', 'BST'],
  'Europe/Paris': ['Paris', 'France', 'CET', 'CEST'],
  'Europe/Berlin': ['Berlin', 'Germany', 'CET', 'CEST'],
  'Europe/Rome': ['Rome', 'Italy', 'CET', 'CEST'],
  'Europe/Madrid': ['Madrid', 'Spain', 'CET', 'CEST'],
  'Europe/Amsterdam': ['Amsterdam', 'Netherlands', 'CET', 'CEST'],
  'Europe/Moscow': ['Moscow', 'Russia', 'MSK'],
  'Asia/Tokyo': ['Tokyo', 'Japan', 'JST'],
  'Asia/Shanghai': ['Shanghai', 'China', 'Beijing', 'CST'],
  'Asia/Hong_Kong': ['Hong Kong', 'HKT'],
  'Asia/Seoul': ['Seoul', 'South Korea', 'KST'],
  'Asia/Singapore': ['Singapore', 'SGT'],
  'Asia/Bangkok': ['Bangkok', 'Thailand', 'ICT'],
  'Asia/Jakarta': ['Jakarta', 'Indonesia', 'WIB'],
  'Asia/Manila': ['Manila', 'Philippines', 'PHT'],
  'Asia/Kolkata': ['Kolkata', 'Mumbai', 'Delhi', 'India', 'IST'], // Valid India timezone
  'Asia/Dubai': ['Dubai', 'UAE', 'GST'],
  'Asia/Riyadh': ['Riyadh', 'Saudi Arabia', 'AST'],
  'Asia/Tehran': ['Tehran', 'Iran', 'IRST'],
  'Asia/Jerusalem': ['Jerusalem', 'Israel', 'IST'],
  'Africa/Cairo': ['Cairo', 'Egypt', 'EET'],
  'Africa/Lagos': ['Lagos', 'Nigeria', 'WAT'],
  'Africa/Johannesburg': ['Johannesburg', 'South Africa', 'SAST'],
  'Africa/Nairobi': ['Nairobi', 'Kenya', 'EAT'],
  'Africa/Casablanca': ['Casablanca', 'Morocco', 'WET'],
  'Australia/Sydney': ['Sydney', 'Australia', 'AEST', 'AEDT'],
  'Australia/Melbourne': ['Melbourne', 'Australia', 'AEST', 'AEDT'],
  'Australia/Perth': ['Perth', 'Australia', 'AWST'],
  'Pacific/Auckland': ['Auckland', 'New Zealand', 'NZST', 'NZDT'], // Valid New Zealand timezone
  'Pacific/Honolulu': ['Honolulu', 'Hawaii', 'HST']
};

// Default business hours
export const DEFAULT_BUSINESS_HOURS = {
  startHour: 9,
  endHour: 17,
  intervalMinutes: 30
} as const;
